import React from "react";
import { useTranslation } from "react-i18next";
import "./CSS/MobileTabBar.css";

export type MobileTab = "chat" | "documents" | "counsel";

interface MobileTabBarProps {
  activeTab: MobileTab;
  onTabChange: (tab: MobileTab) => void;
  /** Whether to show the counsel review tab (only for QandA Agent) */
  showCounselReview?: boolean;
  /** Callback when counsel review tab is clicked */
  onCounselReviewClick?: () => void;
  /** Whether there are documents to show (if false, hides documents tab) */
  hasDocuments?: boolean;
  /** Number of documents to display as a badge on the Documents tab */
  documentCount?: number;
}

/**
 * Bottom sticky tab bar for mobile Document Agent views.
 * Allows switching between Chat and Documents panels.
 */
const MobileTabBar: React.FC<MobileTabBarProps> = ({
  activeTab,
  onTabChange,
  showCounselReview = false,
  onCounselReviewClick,
  hasDocuments = true,
  documentCount,
}) => {
  const { t } = useTranslation();

  const handleCounselClick = () => {
    onTabChange("counsel");
    onCounselReviewClick?.();
  };

  return (
    <nav
      className="mobile-tab-bar"
      role="tablist"
      aria-label="Mobile navigation"
    >
      <button
        id="chat-tab"
        className={`mobile-tab-bar__tab ${activeTab === "chat" ? "mobile-tab-bar__tab--active" : ""}`}
        onClick={() => onTabChange("chat")}
        aria-selected={activeTab === "chat" ? "true" : "false"}
        role="tab"
        aria-controls="chat-panel"
      >
        <svg
          className="mobile-tab-bar__icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <span className="mobile-tab-bar__label">
          {t("documentAgent.mobileTab.chat", "Chat")}
        </span>
      </button>

      {hasDocuments && (
        <button
          id="documents-tab"
          className={`mobile-tab-bar__tab ${activeTab === "documents" ? "mobile-tab-bar__tab--active" : ""}`}
          onClick={() => onTabChange("documents")}
          aria-selected={activeTab === "documents" ? "true" : "false"}
          role="tab"
          aria-controls="documents-panel"
        >
          <span className="mobile-tab-bar__icon-wrapper">
            <svg
              className="mobile-tab-bar__icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            {documentCount != null && documentCount > 0 && (
              <span className="mobile-tab-bar__badge">{documentCount}</span>
            )}
          </span>
          <span className="mobile-tab-bar__label">
            {t("documentAgent.mobileTab.documents", "Documents")}
          </span>
        </button>
      )}

      {showCounselReview && (
        <button
          id="counsel-tab"
          className={`mobile-tab-bar__tab ${activeTab === "counsel" ? "mobile-tab-bar__tab--active" : ""}`}
          onClick={handleCounselClick}
          aria-selected={activeTab === "counsel" ? "true" : "false"}
          role="tab"
          aria-controls="counsel-panel"
        >
          <svg
            className="mobile-tab-bar__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {/* Briefcase / lawyer icon */}
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
          </svg>
          <span className="mobile-tab-bar__label">
            {t("documentAgent.mobileTab.counselReview", "Counsel Review")}
          </span>
        </button>
      )}
    </nav>
  );
};

export default MobileTabBar;
